import { useState } from "react";
import { BookOpen, Clock } from "lucide-react";
import { useLang } from "../../i18n/LanguageContext";
import { useJourney } from "../../context/JourneyContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";

// Mirrors backend/guide_content.py (INTRO + step 01) so the preview always
// reflects the opening of the real PDF. Guide body is English (the PDF is
// English); only the surrounding chrome is localised.
const GUIDE = {
  foreign: {
    title: "How to Set Up a Business in Fiji",
    intro:
      "Setting up a business in Fiji as a foreign investor is far more accessible than most people assume. The single most surprising fact is this: almost the entire process can be completed remotely. The only step that genuinely requires you to be physically present in Fiji is opening your corporate bank account.",
    step: {
      number: "01",
      title: "Company Registration",
      timeline: "2–3 working days",
      body:
        "Your Fijian company is registered with the Registrar of Companies. You will choose a company name (a name search and reservation is done first), define your share structure, and appoint shareholders and directors.",
      docs: [
        "Certified copy of passport or driver's licence for each shareholder and director",
        "Certified copy of birth certificate for each shareholder and director",
        "Proposed company name (with 1–2 alternatives in case of a clash)",
      ],
      tip:
        "Reserve your company name early, even before your documents are fully certified. Name clashes are the most common cause of avoidable delay.",
    },
  },
  local: {
    title: "How to Set Up a Business in Fiji (Local Citizens)",
    intro:
      "As a Fijian citizen, setting up your company is straightforward and can be largely completed with the right guidance. This guide covers company formation, tax registration and opening your business bank account, with realistic timelines, documents and costs.",
    step: {
      number: "01",
      title: "Company Registration",
      timeline: "2–3 working days",
      body:
        "Register your company with the Registrar of Companies with local shareholder emphasis. Choose your name, define your share structure, and appoint directors and shareholders. This can be completed remotely with our support.",
      docs: [
        "Certified copy of a valid Fiji ID or passport for each shareholder and director",
        "TIN for each individual (or applied for in parallel)",
        "Proposed company name (with 1–2 alternatives)",
      ],
      tip: "Reserve your company name early to avoid the most common cause of delay.",
    },
  },
};

const CHROME = {
  en: { cta: "Preview the guide", badge: "Free guide · Page 1", docs: "Documents required", tip: "Insider tip", note: "This is the opening of the guide you'll receive by email." },
  zh: { cta: "预览指南", badge: "免费指南 · 第 1 页", docs: "所需文件", tip: "内部提示", note: "这是您将通过电子邮件收到的指南开头部分。" },
  hi: { cta: "गाइड देखें", badge: "मुफ़्त गाइड · पृष्ठ 1", docs: "आवश्यक दस्तावेज़", tip: "अंदरूनी सुझाव", note: "यह उस गाइड की शुरुआत है जो आपको ईमेल पर मिलेगी।" },
  ja: { cta: "ガイドをプレビュー", badge: "無料ガイド · 1ページ目", docs: "必要書類", tip: "インサイダーのヒント", note: "これはメールでお届けするガイドの冒頭部分です。" },
  ko: { cta: "가이드 미리보기", badge: "무료 가이드 · 1페이지", docs: "필요 서류", tip: "내부 팁", note: "이메일로 받으실 가이드의 첫 부분입니다." },
  es: { cta: "Vista previa de la guía", badge: "Guía gratuita · Página 1", docs: "Documentos requeridos", tip: "Consejo de experto", note: "Este es el comienzo de la guía que recibirás por correo." },
  fr: { cta: "Aperçu du guide", badge: "Guide gratuit · Page 1", docs: "Documents requis", tip: "Conseil d'initié", note: "Voici le début du guide que vous recevrez par e-mail." },
  ar: { cta: "معاينة الدليل", badge: "دليل مجاني · صفحة 1", docs: "المستندات المطلوبة", tip: "نصيحة من الخبراء", note: "هذه بداية الدليل الذي ستستلمه عبر البريد الإلكتروني." },
};

export const GuidePreview = () => {
  const { lang } = useLang();
  const { journey } = useJourney();
  const [open, setOpen] = useState(false);
  const g = GUIDE[journey === "local" ? "local" : "foreign"];
  const c = CHROME[lang] || CHROME.en;

  return (
    <>
      <button
        type="button"
        data-testid="guide-preview-trigger"
        onClick={() => setOpen(true)}
        className="mt-6 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-cream/90 underline underline-offset-4 hover:text-cream transition-colors"
      >
        <BookOpen className="h-4 w-4" /> {c.cta}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          data-testid="guide-preview-modal"
          className="max-w-2xl max-h-[85vh] overflow-y-auto bg-[var(--page)] text-[var(--ink)] border border-[var(--hairline)]"
        >
          <DialogHeader>
            <span className="text-[11px] uppercase tracking-[0.25em] text-[var(--muted-ink)] font-semibold">{c.badge}</span>
            <DialogTitle className="font-display text-2xl sm:text-3xl leading-tight tracking-tight mt-1">{g.title}</DialogTitle>
          </DialogHeader>

          <p className="text-[15px] leading-relaxed text-[var(--ink)]/85">{g.intro}</p>

          <div className="mt-4 border-t border-[var(--hairline)] pt-5">
            <div className="text-[11px] uppercase tracking-[0.2em] text-[var(--muted-ink)] font-semibold">Step {g.step.number}</div>
            <div className="mt-1 font-display text-xl">{g.step.title}</div>
            <div className="mt-1 inline-flex items-center gap-1.5 text-sm text-[var(--muted-ink)]">
              <Clock className="h-3.5 w-3.5" /> {g.step.timeline}
            </div>
            <p className="mt-3 text-[15px] leading-relaxed text-[var(--ink)]/85">{g.step.body}</p>

            <div className="mt-4 text-sm font-semibold">{c.docs}</div>
            <ul className="mt-2 list-disc pl-5 space-y-1 text-sm text-[var(--ink)]/80">
              {g.step.docs.map((d, i) => <li key={i}>{d}</li>)}
            </ul>

            <div className="mt-4 text-sm leading-relaxed border-l-2 border-[var(--ink)] bg-[var(--ink)]/[0.04] px-4 py-3">
              <span className="font-semibold">{c.tip}:</span> {g.step.tip}
            </div>
          </div>

          <p className="mt-4 text-xs text-[var(--muted-ink)] italic" data-testid="guide-preview-note">{c.note}</p>
        </DialogContent>
      </Dialog>
    </>
  );
};
